import React, { useEffect, useState } from 'react';
import {
    Autocomplete, Button, Dialog, DialogActions, DialogContent, DialogTitle,
    FormControlLabel, Stack, Switch, TextField, Typography,
} from '@mui/material';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '../../../firebase';
import { createAuction } from '../../../services/auctions';
import { useNotification } from '../../../context/NotificationContext';

/**
 * De administrator maakt een subleague aan en wijst er meteen een beheerder aan.
 * Dezelfde schrijfactie als /start-auction, via services/auctions.js, zodat beide
 * ingangen exact hetzelfde document opleveren.
 */
function NewSubleagueDialog({ open, onClose, onCreated, seasonId, seasonName }) {
    const notify = useNotification();
    const [name, setName] = useState('');
    const [admin, setAdmin] = useState(null);
    const [isOpenAuction, setIsOpenAuction] = useState(true);
    const [adminPlays, setAdminPlays] = useState(true);
    const [users, setUsers] = useState([]);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (!open) return;

        const loadUsers = async () => {
            try {
                const snapshot = await getDocs(collection(db, 'users'));
                setUsers(snapshot.docs
                    .map((d) => ({ uid: d.id, ...d.data() }))
                    .sort((a, b) => (a.name || a.email || '').localeCompare(b.name || b.email || '')));
            } catch (err) {
                console.error('Kon gebruikers niet ophalen:', err);
                notify.error('De lijst met gebruikers kon niet opgehaald worden.');
            }
        };
        loadUsers();
    }, [open, notify]);

    const reset = () => {
        setName('');
        setAdmin(null);
        setIsOpenAuction(true);
        setAdminPlays(true);
    };

    const handleClose = () => {
        if (saving) return;
        reset();
        onClose();
    };

    const handleCreate = async () => {
        setSaving(true);
        try {
            const auctionId = await createAuction({
                name,
                seasonId,
                admin: { uid: admin?.uid, email: admin?.email, name: admin?.name },
                isOpen: isOpenAuction,
                addAdminAsParticipant: adminPlays,
            });
            reset();
            onCreated(auctionId);
        } catch (err) {
            console.error('Kon subleague niet aanmaken:', err);
            notify.error(err.message || 'De subleague kon niet aangemaakt worden.');
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
            <DialogTitle>Subleague aanmaken</DialogTitle>
            <DialogContent>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                    De subleague speelt op {seasonName}. De beheerder regelt daarna zelf de
                    deelnemers, het budget en de veilingavond.
                </Typography>

                <Stack spacing={3} sx={{ mt: 1 }}>
                    <TextField
                        label="Naam van de subleague"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        fullWidth
                        required
                    />
                    <Autocomplete
                        options={users}
                        value={admin}
                        onChange={(event, value) => setAdmin(value)}
                        getOptionLabel={(option) => `${option.name || '(geen naam)'} — ${option.email}`}
                        isOptionEqualToValue={(option, value) => option.uid === value.uid}
                        renderInput={(params) => (
                            <TextField {...params} label="Beheerder" required />
                        )}
                    />
                    <FormControlLabel
                        control={<Switch checked={isOpenAuction} onChange={(e) => setIsOpenAuction(e.target.checked)} />}
                        label={isOpenAuction
                            ? 'Open subleague (iedereen kan meedoen)'
                            : 'Besloten subleague (deelnemers hebben goedkeuring nodig)'}
                    />
                    <FormControlLabel
                        control={<Switch checked={adminPlays} onChange={(e) => setAdminPlays(e.target.checked)} />}
                        label="Beheerder doet ook mee als deelnemer"
                    />
                </Stack>
            </DialogContent>
            <DialogActions>
                <Button onClick={handleClose} disabled={saving}>Annuleren</Button>
                <Button
                    variant="contained"
                    onClick={handleCreate}
                    disabled={saving || !name.trim() || !admin}
                >
                    {saving ? 'Bezig…' : 'Subleague aanmaken'}
                </Button>
            </DialogActions>
        </Dialog>
    );
}

export default NewSubleagueDialog;
